import React, { useState } from 'react';
import Grid from '@mui/material/Grid2';
import { Box, List, ListItemButton, ListItemText, Divider } from '@mui/material';
import { Edit } from '@mui/icons-material';
import InlineReportCountdown from "./inline_report_countdown";
import ReportCountdown from "./report_countdown";
import DamReportStepper from "./dam_report_stepper";
import CommonCard from './common_card';
import { formatReportDate } from "../func/util";
import { REPORTING_TIME, REPORT_STATUS } from "../const/config";
import { TypographyStepTitle } from '../style/typography';
import { RequestDetailButton } from "../style/button";

const DamCompatibilityStatus = ({ title, earthquake, onReportClick, type, reportList, reportDetailList, selectedItem, setSelectedItem, isButtonDisabled }) => {

  // 左側選択に応じた右側のリストを返す関数
  const getRightColumnItems = () => {
    switch (selectedItem) {
      case 'first': // 初動対応
        return [
          { key: 'ft_checked', label: '情報確認', date: reportDetailList?.ft_checked_at, name: reportDetailList?.ft_checked_name },
          { key: 'ft_reported_to_leader', label: 'グループ長報告', date: reportDetailList?.ft_reported_to_leader_at, name: reportDetailList?.ft_reported_to_leader_name },
          { key: 'ft_reported', label: '国交省への報告', date: reportDetailList?.ft_reported_at, name: reportDetailList?.ft_reported_name },
          { key: 'ft_reported_to_internal', label: 'Teamsへの報告', date: reportDetailList?.ft_reported_to_internal_at, name: reportDetailList?.ft_reported_to_internal_name }
        ];
      case 'initial': // 第1報
        return [
          { key: 'ir_created', label: '報告書作成', date: reportDetailList?.ir_created_at, name: reportDetailList?.ir_created_name },
          { key: 'ir_approved', label: '報告書承認', date: reportDetailList?.ir_approved_at, name: reportDetailList?.ir_approved_name },
          { key: 'ir_reported', label: '国交省への報告', date: reportDetailList?.ir_reported_at, name: reportDetailList?.ir_reported_name },
          { key: 'ir_reported_to_internal', label: '本店への報告', date: reportDetailList?.ir_reported_to_internal_at, name: reportDetailList?.ir_reported_to_internal_name }
        ];
      case 'primary': // 1次点検
        return [
          { key: 'pi_created', label: '報告書作成', date: reportDetailList?.pi_created_at, name: reportDetailList?.pi_created_name },
          { key: 'pi_approved', label: '報告書承認', date: reportDetailList?.pi_approved_at, name: reportDetailList?.pi_approved_name },
          { key: 'pi_reported', label: '国交省への報告', date: reportDetailList?.pi_reported_at, name: reportDetailList?.pi_reported_name },
          { key: 'pi_reported_to_internal', label: '本店への報告', date: reportDetailList?.pi_reported_to_internal_at, name: reportDetailList?.pi_reported_to_internal_name }
        ];
      case 'secondary': // 2次点検
        return [
          { key: 'si_created', label: '報告書作成', date: reportDetailList?.si_created_at, name: reportDetailList?.si_created_name },
          { key: 'si_approved', label: '報告書承認', date: reportDetailList?.si_approved_at, name: reportDetailList?.si_approved_name },
          { key: 'si_reported', label: '国交省への報告', date: reportDetailList?.si_reported_at, name: reportDetailList?.si_reported_name },
          { key: 'si_reported_to_internal', label: '本店への報告', date: reportDetailList?.si_reported_to_internal_at, name: reportDetailList?.si_reported_to_internal_name }
        ];
      default:
        return [];
    }
  };

  const handleSelect = (key) => {
    setSelectedItem(key);
  };

  return (
    <Grid size={{ xs: 12, sm: 12, md: 6 }}>
      <Grid container size={{ xs: 12 }}>
        <Grid size={{ xs: 7 }}>
          <TypographyStepTitle sx={{ fontSize: { xs: "1.4rem", sm: "1.5rem", md: "1.5rem" } }}>{title}</TypographyStepTitle>
        </Grid>
        <Grid size={{ xs: 5 }} display="flex" justifyContent="flex-end">
          {/* 「途中終了」または「完了」の場合はボタン非表示 */}
          {!isButtonDisabled && (
              <RequestDetailButton
                size="large"
                startIcon={<Edit />}
                onClick={() => onReportClick(reportDetailList, type)}
              >
                報告作業を開始する
              </RequestDetailButton>
            )}
        </Grid>
      </Grid>
      <CommonCard
        child={
          <Box sx={{ padding: 2 }}>
            <DamReportStepper reportDetailList={reportDetailList} />
            <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', mt: 2 }}>
              {/* Left Column */}
              <List sx={{
                width: '48%',
                '& > .Mui-selected': {
                  backgroundColor: 'rgba(10, 124, 183, 1.0) !important',
                  color: '#fff',
                  fontWeight: 'bold',
                  position: 'relative'
                },
                '& > .Mui-selected::after': {
                  content: '""',
                  position: 'absolute',
                  left: '100%',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 0,
                  height: 0,
                  borderLeft: '10px solid rgba(10, 124, 183, 1.0)',
                  borderBottom: '10px solid transparent',
                  borderTop: '10px solid transparent'
                },
                '& > .Mui-selected:hover': {
                  backgroundColor: 'rgba(10, 124, 183, 0.8) !important'
                },
                '& .MuiListItemText-primary': { fontWeight: 'bold' },
                '& > .Mui-selected .MuiListItemText-secondary': { color: '#fff' }
              }}>
                {[
                  { key: 'first', label: '初動対応', date: reportList?.first_task_sent_at, reportTime: REPORTING_TIME.INITIALTASK },
                  { key: 'initial', label: '第1報', date: reportList?.initial_report_sent_at, reportTime: REPORTING_TIME.FISRTREPORT },
                  { key: 'primary', label: '1次点検', date: reportList?.primary_inspection_sent_at, reportTime: REPORTING_TIME.PRIMARYCHECK },
                  { key: 'secondary', label: '2次点検', date: reportList?.secondary_inspection_sent_at, reportTime: REPORTING_TIME.SECONDARYCHECK }
                ].map((item, idx, arr) => {
                  // カウントダウン表示制御
                  let showCountdown = false;
                  if (!item.date) {
                    if (item.key === 'first') {
                      showCountdown = true;
                    } else if (item.key === 'initial' && arr[0].date) {
                      showCountdown = true;
                    } else if (item.key === 'primary' && arr[1].date) {
                      showCountdown = true;
                    } else if (item.key === 'secondary' && arr[2].date) {
                      showCountdown = true;
                    }
                  }

                  return (
                    <React.Fragment key={item.key}>
                      <ListItemButton
                        selected={selectedItem === item.key}
                        onClick={() => handleSelect(item.key)}
                      >
                        <ListItemText
                          primary={item.label}
                          secondary={
                            item.date
                              ? `完了日時: ${formatReportDate(item.date)}`
                              : showCountdown
                                ? <InlineReportCountdown
                                    occued_at={earthquake.occured_at}
                                    addedMinutes={item.reportTime}
                                  />
                                : "完了日時: -"
                          }
                        />
                      </ListItemButton>
                      {idx < 3 && (
                        <Divider sx={{ marginTop: 0.5, marginBottom: 0.5 }} />
                      )}
                    </React.Fragment>
                  );
                })}
              </List>
              {/* Right Column */}
              <List sx={{ width: '50%', marginLeft: '2%' }}>
                {getRightColumnItems().map((item, idx) => (
                  <React.Fragment key={item.key}>
                    <ListItemButton
                      sx={{
                        backgroundColor: selectedItem === item.key ? 'rgba(10, 124, 183, 1.0)' : 'rgba(10, 124, 183, 0.2)',
                        color: selectedItem === item.key ? '#fff' : 'inherit'
                      }}
                    >
                      <ListItemText
                        primary={item.label}
                        secondary={
                          <>
                            完了日時: {formatReportDate(item.date) ?? '-'} / 担当者:  {item.name ?? '-'}
                          </>
                        }
                        secondaryTypographyProps={{ color: selectedItem === item.key ? '#fff' : 'inherit' }}
                      />
                    </ListItemButton>
                    {idx < 3 && (
                      <Divider sx={{ marginTop: 0.5, marginBottom: 0.5 }} />
                    )}
                  </React.Fragment>
                ))}
              </List>
            </Box>
          </Box>
        }
      />
    </Grid>
  );
};

export default DamCompatibilityStatus;